// Client for the cloud API. Types of vault data come from the cloud's protocol definitions.
import type { Bucket, ClassSnapshot, FieldSnapshot, HistogramBucket, HostInfo, HostSample, ModelSnapshot, TlsInfo, VaultStatus } from '../../../cloud/src/protocol.ts';

import { getEnvId, takesEnv, type AuditEntry, type Environment } from './env.ts';
import type { CatalogData, CatalogEntry, CatalogStatus, Sensitivity } from './catalog.ts';
import type { SystemInfo, SystemRoute, SystemStatus } from './systems.ts';

export type { AuditEntry, Environment };
export type { Bucket, ClassSnapshot, FieldSnapshot, HistogramBucket, HostInfo, HostSample, ModelSnapshot, TlsInfo, VaultStatus };

export type Role = 'admin' | 'owner' | 'viewer';

export interface User {
  id: string; email: string; name: string; role: Role; tenantId: string | null;
  createdAt: number; lastLoginAt: number | null;
}

export interface Tenant { id: string; name: string; createdAt: number }

export interface Me { user: User; tenants: Tenant[] }

export interface VaultInfo {
  id: string; tenantId: string; name: string; registeredAt: number; lastSeen: number | null;
  version: string | null; os: string | null; hostname: string | null; revokedAt: number | null;
  status: VaultStatus | null; online: boolean; connectedSince: number | null;
  /** The address senders use: the manual override, else what the vault reports. */
  ingestUrl: string | null; reportedIngestUrl: string | null; ingestUrlOverride: string | null;
  environmentId: string;
  /** What the vault calls itself (`name` is the effective one: the custom name when set). */
  reportedName: string; description: string | null; host: HostInfo | null; noteCount: number;
  /** How many connector keys reach this vault (the list; the detail has them in full). */
  connectorKeyCount?: number;
  /** The TLS ingest listener as reported (status.tls) with the ACME state; null when TLS is off. May lack the certificate facts before the first issue. */
  tls?: TlsInfo | null;
  /** Managed configuration at a glance (the list and the detail); absent from an older Cloud. */
  config?: ConfigSummary;
}

export type ConfigState = 'applied' | 'pending-restart' | 'rejected' | 'rolled-back';
export type SettingType = 'int' | 'bool' | 'string' | 'enum' | 'path' | 'secret';
export type SettingValue = string | number | boolean;

/** One setting the Vault describes about itself; the Configuration form is generated from these. */
export interface ConfigSetting {
  key: string; type: SettingType; label: string; help?: string; group?: string; default?: SettingValue | null;
  min?: number; max?: number; options?: string[]; requiresRestart?: boolean; sensitive?: boolean; readOnly?: boolean;
}

export interface LastStart { at: number; reason: string; rolledBack: null | { from: number; to: number; reason: string } }

/** The Vault's own report of its settings (secret values arrive as 'set' / 'unset'). */
export interface ConfigReportView {
  version: number; schema: ConfigSetting[];
  local: Record<string, SettingValue>; overrides: Record<string, SettingValue>; effective: Record<string, SettingValue>;
  state: ConfigState; restartRequired: string[]; errors: { key?: string; message: string }[];
  canRestart: boolean; lastStart: LastStart | null;
}

export interface VaultConfigData {
  report: ConfigReportView | null; reportedAt: number | null;
  desired: { version: number; overrides: Record<string, SettingValue>; updatedBy: string | null; updatedAt: number } | null;
  history: { version: number; at: number; user: string | null; changedKeys: string[] }[];
  online: boolean;
}

export interface ConfigSummary {
  state: ConfigState | 'out-of-date' | null; pendingRestart: string[]; version: number | null; desiredVersion: number | null;
  canRestart: boolean | null; lastStart: { at: number; reason: string; rolledBack: unknown } | null;
}

/** The vault with the load samples of about the last hour (empty after a Cloud restart). */
export interface VaultDetailData extends VaultInfo {
  history: HostSample[];
  /** The connector keys that can send here. */
  connectorKeys?: VaultKey[];
}

export interface VaultKey { keyId: string; name: string; scope: 'environment' | 'vaults'; lastUsedAt: number | null }

export interface VaultNote { id: number; at: number; user: string | null; text: string }

/** Where a key sends: every vault of its environment (also later ones), or only the listed ones (those still in the environment). */
export interface KeyScope { kind: 'environment' | 'vaults'; vaults: { id: string; name: string }[] }

export interface ConnectorKey {
  keyId: string; name: string; createdAt: number; lastUsedAt: number | null; environmentId: string;
  /** The chosen vaults; null for the whole environment. */
  vaultIds: string[] | null; scope: KeyScope;
}

export interface EnvironmentChange { name?: string; color?: string; position?: number; protected?: boolean; isDefault?: boolean }

/** A search hit: the stored message summary plus the vault it came from. */
export interface Hit {
  uuid: string; messageClass: string; type: string; timestamp: number;
  meta: Record<string, string>; flags: string[]; parseError?: string; typeErrors?: string[];
  fieldCount: number; payloadSize?: number; day: string; vaultId: string; vaultName: string;
  /** Fields whose value matched the searched words: [path, value]. */
  matches?: [string, string][];
  /** Values of the fields asked for with column=. */
  columns?: Record<string, string>;
}

export interface ModelsImportReport {
  models: { name: string; result: 'created' | 'updated' | 'unchanged' | 'conflict'; versionsAdded: number; deployed: string[]; notes: string[] }[];
}
export interface RebuildStatus {
  /** The data model (message class) the job is about; null/absent = the whole data model. */
  class?: string | null;
  state: 'idle' | 'running' | 'done' | 'failed' | 'cancelled';
  startedAt?: number; finishedAt?: number; partitionsTotal?: number; partitionsDone?: number; docsScanned?: number; docsTotalEstimate?: number;
  fieldsBefore?: number; fieldsAfter?: number; classesBefore?: number; classesAfter?: number; removedFields?: number; error?: string;
}
export interface RebuildVault { vaultId: string; vaultName: string; status?: RebuildStatus; error?: string }

export interface VaultError { vaultId: string; vaultName: string; status: number; error: string }

export interface SearchResult {
  total: number; tookMs: number; vaults: number; partial: boolean; vaultErrors: VaultError[];
  hits: Hit[]; facets: Record<string, Bucket[]>; fieldFacets: Record<string, Bucket[]>;
  histogramInterval: string | null; histogram: HistogramBucket[]; warnings: string[];
}

/** A value linking messages across systems (see the cloud's relay.related). */
export interface RelatedKey {
  value: string; paths: string[]; classes: string[]; seedDocs: number; docs: number; hop: number; common: boolean;
}

export interface Related {
  q: string; query: string; seedTotal: number; keys: RelatedKey[]; truncated: boolean; tookMs: number; partial: boolean; vaultErrors: VaultError[];
  /** The matches and everything linked to them. */
  search: SearchResult;
  /** Facets of the failed messages among them. */
  failedFacets: Record<string, Bucket[]>;
}

// ---------------------------------------------------------------- overview (see the cloud's overview.ts)

export interface FieldValue { value: string; count: number; first: number; last: number }

export interface OverviewCell {
  system: string; value: string; state: 'same' | 'format' | 'differs'; history: FieldValue[]; changedAt: number | null;
  sources: { cls: string; path: string; current: string; last: number }[];
  failure?: { info: string; at: number };
}

export interface OverviewAttribute {
  label: string; family: string | null; fields: string[]; consensus: string;
  state: 'agree' | 'conflict' | 'single'; agreeing: number; cells: OverviewCell[]; changedAt: number | null;
}

export type FieldRole = 'id' | 'number' | 'category' | 'constant' | 'date' | 'text';

export interface KindField {
  path: string; leaf: string; role: FieldRole; family: string | null; present: number; distinct: number; more: boolean;
  values: FieldValue[]; numeric: { min: number; max: number; sum: number; count: number } | null;
}

export interface Kind {
  cls: string; system: string; total: number; sampled: number; direct: boolean; via: string[];
  status: Bucket[]; format?: string | null; histogram: HistogramBucket[]; first: number | null; last: number | null; fields: KindField[];
}

export interface WallSource {
  system: string; cls: string; path: string; values: FieldValue[]; present: number; distinct: number;
  failure?: { info: string; at: number };
}

export interface Variant { value: string; systems: string[]; formats: string[] }

/** One attribute = value tile of the data view (see the cloud's wall.ts). */
export interface Tile {
  id: string; label: string; family: string | null; fields: string[]; kind: 'value' | 'many';
  value: string; values: FieldValue[]; distinct: number; variants: Variant[];
  state: 'shared' | 'conflict' | 'single'; systems: string[]; classes: string[]; sources: WallSource[];
  first: number; last: number;
  /** The searched value itself. */
  anchor?: boolean;
  /** Characteristic of what was found (not a value that is everywhere anyway). */
  specific?: boolean;
  /** Messages found with this value / messages with it in the whole time range. */
  found?: number; everywhere?: number;
}

/** An attribute whose values split what was found: a way to narrow it down. */
export interface Facet {
  id: string; label: string; family: string | null; paths: { cls: string; path: string; system: string }[];
  values: { value: string; count: number }[]; distinct: number; more: boolean; systems: string[];
}

export interface ModelLink {
  a: { cls: string; path: string; system: string }; b: { cls: string; path: string; system: string };
  shared: number; example: string; searches?: number;
}

/** Records whose next step never arrived (see the cloud's gaps.ts). */
export interface Gap {
  from: { cls: string; path: string }; to: { cls: string; path: string };
  checked: number; linked: number; pending: number; missing: { value: string; since: number }[];
  typicalMs: number | null; lateAfterMs: number;
}

export interface LensCell { value: string; state: 'agree' | 'conflict' | 'single'; systems: { system: string; value: string; at: number; cls: string; same: boolean }[] }
export interface LensEntity { key: string; title: string; systems: string[]; records: number; conflicts: number; last: number; cells: Record<string, LensCell> }
export interface Lens {
  model: { id: string; name: string }; key: string; entities: LensEntity[]; total: number; comparable: number;
  fields: { label: string; family: string | null; compared: number; agree: number; conflicts: number }[];
  partial: boolean; vaultErrors: VaultError[]; tookMs: number;
}

export interface EntityMatch {
  model: { id: string; name: string; version: number }; key: string; fields: Lens['fields'];
  /** All entities the search finds in the model; `entities` has the best ones. */
  total: number; entities: LensEntity[];
}
export interface EntityLayer { matches: EntityMatch[]; partial: boolean; vaultErrors: VaultError[]; tookMs: number }

export interface VirtualModel {
  id: string; name: string; fields: { label: string; paths: { cls: string; path: string; system?: string }[] }[];
  createdAt: number; updatedAt: number;
  /** The latest published version; null while only a draft exists. */
  version?: number | null;
  /** The draft differs from the latest version (or nothing was published). */
  dirty?: boolean;
  /** Environment id → the version it runs. */
  deployed?: Record<string, number>;
}

/** A data model of your own the Cloud suggests (docs/model-suggestions.md); the id changes when its sources do. */
export interface ModelSuggestion {
  id: string; name: string; confidence: number; isNew: boolean;
  classes: { cls: string; system: string | null; displayName: string }[];
  fields: { label: string; kind: string; sources: { cls: string; path: string; system: string | null }[]; evidence: string; confidence: number }[];
  evidence: string[];
}
export interface ModelSuggestions { suggestions: ModelSuggestion[]; computedAt: number; partial: boolean; dismissed: number }

export interface ModelContent { name: string; fields: VirtualModel['fields'] }
export interface FieldChange { label: string; addedPaths: string[]; removedPaths: string[] }
export interface ModelDiff {
  initial: boolean; renamed: { from: string; to: string } | null;
  addedFields: string[]; removedFields: string[]; changedFields: FieldChange[]; same: boolean;
}
export interface ModelVersion {
  id: string; version: number; comment: string | null; createdBy: string | null; createdAt: number;
  content: ModelContent; diff: ModelDiff; deployedTo: string[];
}
export interface ModelVersions { draft: { dirty: boolean; diff: ModelDiff }; versions: ModelVersion[] }
export interface DriftWarning { kind: 'no_data' | 'class_missing' | 'field_missing' | 'type_mixed'; message: string; cls?: string; path?: string; field?: string }
export interface DeployItem { modelId: string; version: number }
export interface DeployPreview {
  environment: { id: string; name: string; protected: boolean };
  items: { modelId: string; name: string; version: number; current: number | null; unchanged: boolean; diff: ModelDiff; drift: DriftWarning[] }[];
}
export interface DeployResult { environmentId: string; items: { modelId: string; name: string; from: number | null; to: number }[] }

export interface Identity { family: string | null; label: string; value: string; fields: string[]; systems: string[]; searched: boolean }

export interface Overview {
  q: string; type: string; title: string; identifiedBy: Identity[]; total: number;
  window: { first: number | null; last: number | null };
  systems: { code: string; messages: number; kinds: number }[];
  timeline: { interval: string | null; buckets: { time: number; bySystem: Record<string, number> }[] };
  attributes: OverviewAttribute[]; kinds: Kind[]; wall: Tile[]; facets: Facet[]; modelLinks: ModelLink[]; ref: string;
  links: { identifiers: number; truncated: boolean };
  tookMs: number; partial: boolean; vaultErrors: VaultError[];
}

export interface Message extends Omit<Hit, 'fieldCount' | 'vaultId' | 'vaultName'> {
  fields: [string, string][];
}

export interface ExecutionStep {
  correlationId: string | null; timestamp: number; status?: string; messageClass: string; info?: string;
  elapsedMs?: number; items: Hit[]; vaultId: string; vaultName: string;
}

export interface Execution {
  executionId: string; messages: number; steps: ExecutionStep[]; partial: boolean; vaultErrors: VaultError[];
}

// ---------------------------------------------------------------- records (see the cloud's records.ts)

export interface RecordKind { cls: string; system: string; key: string[]; label: string; records: number; more?: boolean; messages: number }
export interface RecordStep {
  cls: string; system: string; via: [string, string]; typicalMs: number | null; established: boolean; lateAfterMs: number;
}
/** When the step was first seen; 0 = exists outside what was found; pending = not late yet; null = not expected or known. */
export type StepCell = number | 'pending' | 'missing' | null;
export interface RecordRow {
  key: string; first: number; last: number; messages: number; status: string | null; failed: number;
  values: Record<string, string>; steps: StepCell[];
}
export interface RecordList extends RecordKind {
  /** How often a record usually gets a new document, for kinds sent again and again (master data); null for once-through records. */
  cadenceMs: number | null;
  columns: { path: string; label: string; role: string }[]; steps: RecordStep[]; rows: RecordRow[]; truncated: boolean;
}
export interface Records { kinds: RecordKind[]; list: RecordList | null; tookMs: number; partial: boolean; vaultErrors: VaultError[] }

// ---------------------------------------------------------------- card (see the cloud's card.ts)

export interface CardSource { system: string; cls: string; path: string; at: number }
export interface CardField { label: string; family: string | null; values: { value: string; sources: CardSource[] }[]; conflict: boolean }
export interface CardSection {
  id: string; label: string; kind: 'party' | 'address' | 'terms' | 'record' | 'group' | 'rows';
  systems: string[]; classes: string[]; fields: CardField[];
  table?: { key: string; columns: { label: string; system: string; cls: string; path: string }[]; rows: { key: string; cells: (string | null)[] }[] };
}
export interface Card {
  sections: CardSection[];
  messages: { vaultId: string; uuid: string; day: string; cls: string; system: string; type?: string | null; status: string | null; info?: string | null; at: number; executionId: string | null; process: string | null }[];
  tookMs: number; partial: boolean; vaultErrors: VaultError[];
}

// ---------------------------------------------------------------- history (see the cloud's history.ts)

export interface HistoryEvent {
  at: number; cls: string; system: string; record: string | null; process: string | null; status: string | null;
  info: string | null; executionId: string | null; created: boolean;
  changes: { path: string; from: string | null; to: string }[];
  message: { vaultId: string; uuid: string; day: string };
}
export interface History { events: HistoryEvent[]; messages: number; truncated: boolean; tookMs: number; partial: boolean; vaultErrors: VaultError[] }

// ---------------------------------------------------------------- runs (see the cloud's relay.runs)

export interface Run {
  executionId: string; process: string | null; system: string | null; first: number; last: number;
  /** Messages found of the run, all its messages, and how many failed. */
  messages: number; total: number; failed: number; statuses: Record<string, number>; classes: string[];
}
export interface Runs {
  matched: number; withoutRun: number; runs: number; truncated: boolean; list: Run[];
  processes: { process: string; system: string | null; runs: number; failed: number; messages: number }[];
  vaultErrors: VaultError[];
}

// ---------------------------------------------------------------- activity, preview, suggest (see the cloud's activity.ts, preview.ts, suggest.ts)

export interface ActivityClass {
  cls: string; system: string;
  /** The data format its documents mostly come in (json, xml, csv …). */
  format: string | null;
  total: number; failed: number;
  /** When its newest document came. */
  last: number | null;
}

/**
 * How often an integration runs, from a week of its starts, computed by the cloud (see the cloud's rhythm.ts:
 * the same classification as lib/rhythm.ts rhythmOf(), plus `late`). Hours of the day are in the `tz` the
 * activity was asked with, else the cloud's own time zone.
 */
export interface FlowRhythm {
  kind: 'hourly' | 'daily' | 'every' | 'events' | 'once';
  /** "daily at 18:00", "on events, about 6 a day" */
  label: string;
  /** "daily 18:00", "≈ 6/day" */
  short: string;
  scheduled: boolean;
  everyMs: number | null;
  perDay: number;
  /** Runs started in each hour of the day, over all days. */
  hours: number[];
  last: number | null;
  /** When the next run is due, for a schedule. */
  next: number | null;
  /** A schedule whose next run is overdue by more than a quarter of its interval (5 min at least). */
  late: boolean;
}

/** An integration: a process that moves data from the data model of its own system to the others. */
export interface Flow {
  process: string;
  /** The system of the process, from its [PREFIX]. */
  system: string | null;
  from: string; to: string[]; runs: number; failed: number; first: number; last: number;
  /** The data models in the order its runs touch them, and those of them it reads (a read starts a new source). */
  path?: string[]; reads?: string[];
  /** Its steps in order: a data model and whether it was read there. */
  steps?: [string, boolean][];
  /** When its runs started in the window, oldest first: for telling its rhythm (see lib/rhythm.ts). */
  starts: number[];
  /** Its rhythm from a week of starts (a daily job ran once in the window): hourly at :05, daily at 18:00, on events. */
  rhythm?: FlowRhythm;
}

/** An integration whose newest run has a failed document, and so do the runs before it back to `since`. */
export interface Failing {
  process: string;
  system: string | null;
  /** When the first run of the streak started. */
  since: number;
  /** Failed documents since then. */
  count: number;
  /** The newest failed document. */
  newestRef: { vaultId: string; uuid: string; day: string };
}

export interface ActivityDoc {
  vaultId: string; uuid: string; day: string; cls: string; system: string; type: string | null;
  status: string | null; process: string | null; executionId: string | null; info: string | null; at: number;
}

/** An integration that has not run for much longer than it usually waits between runs. */
export interface Silent { process: string; last: number; usualMs: number }

/** The tenant now: what moved in the window (the last day by default). */
export interface Activity {
  from: number | null; to: number; total: number; failed: number;
  byClass: ActivityClass[]; flows: Flow[];
  /** The newest documents, newest first (at most 50). */
  latest: ActivityDoc[];
  silent: Silent[];
  /** The integrations failing now, the longest first (see the cloud's activity.ts failingOf). */
  failing?: Failing[];
  histogramInterval: string | null; histogram: HistogramBucket[];
  tookMs: number; partial: boolean; vaultErrors: VaultError[];
}

export interface PreviewClass { cls: string; system: string; total: number; last: number | null }

/** What a search is, fast: from its scope alone, before the overview is ready. */
export interface Preview {
  q: string;
  /** Customer, Order, Postcode... */
  type: string;
  title: string;
  /** The search itself and the identities found with it. */
  identities: Identity[];
  systems: { code: string; messages: number; kinds: number }[];
  classes: PreviewClass[];
  total: number; first: number | null; last: number | null;
  histogramInterval: string | null; histogram: HistogramBucket[];
  tookMs: number; partial: boolean; vaultErrors: VaultError[];
}

export interface SuggestItem {
  value: string;
  /** Where it was seen: the field and data model, or the kind of metadata. */
  detail: string | null;
  count: number;
  /** What an identifier names, when its newest document says: "Nastolan Lattiamestarit Oy" for CUST-41001. */
  title?: string;
}
/** system: a system of the registry (value = its code, detail = its name); integration: a process (value = its name). */
export interface SuggestGroup { kind: 'identifier' | 'field' | 'value' | 'recent' | 'system' | 'integration'; label: string; items: SuggestItem[] }
/** Values that start with what was typed, grouped by what they are. */
export interface Suggest { prefix: string; groups: SuggestGroup[]; tookMs: number; partial: boolean; vaultErrors: VaultError[] }

/**
 * An integration between systems (see the cloud's integrations.ts): a process carrying data from one system to another.
 * One-sided: `to` null = it only reads from `from`, `from` null = it only writes to `to`, `from` = `to` = within one system.
 */
export interface Integration {
  process: string; from: string | null; to: string | null;
  runs: number; documents: number; failed: number; lastRun: number | null; cadenceMs: number | null;
  dataModels: string[]; status: 'ok' | 'failing' | 'quiet';
  /** Every system its runs go through in order, when more than two (each pair of neighbours is a row of its own). */
  path?: string[];
}
export interface Integrations { from: number | null; to: number; systems: string[]; integrations: Integration[]; partial: boolean; vaultErrors: VaultError[] }

/** One bar of a timeline: runs that started in [start, end) and those with a failed document; `known` false = before the runs listed. */
export interface RunBar { start: number; end: number; runs: number; failed: number; known: boolean }
export interface RunRow { executionId: string; first: number; last: number; failed: number; messages: number; total: number; statuses: Record<string, number>; classes: string[] }

/** One integration (see the cloud's systemview.ts): status and cadence over seven days, runs of the window. */
export interface IntegrationDetail {
  process: string; from: number; to: number;
  integrations: Integration[]; systems: string[];
  status: Integration['status'] | null; cadenceMs: number | null; lastRun: number | null;
  nextDue: number | null; lateAt: number | null; dataModels: string[];
  runs: number; failedRuns: number; documents: number; successRate: number | null;
  timeline: RunBar[]; latest: RunRow[]; truncated: boolean; partial: boolean; vaultErrors: VaultError[];
}

export interface FailedDoc { vaultId: string; uuid: string; day: string; at: number; cls: string; process: string | null; executionId: string | null; info: string | null }

/** One system (see the cloud's systemview.ts): its integrations, data models, connections and newest failures. */
export interface SystemOverview {
  code: string; from: number; to: number;
  integrations: Integration[];
  dataModels: { cls: string; documents: number; failed: number }[];
  connections: SystemRoute[]; warnings: { connection: string; namedFor: string; dataSays: string }[];
  failures: FailedDoc[]; partial: boolean; vaultErrors: VaultError[];
}

/** The system registry and, per data model, its system: pinned by an admin or detected from the data. */
export interface SystemsData {
  systems: SystemInfo[];
  classes: Record<string, { system: string; detected: string | null; pinned: boolean }>;
  /** The Vaults took long to answer: the data models per system are not in this answer. */
  detecting: boolean;
  kinds: string[]; icons: string[];
}

/** What an edit of a system may change. */
export interface SystemChange {
  name?: string; code?: string; kind?: string | null; color?: string | null; icon?: string | null; group?: string | null; description?: string | null;
  owner?: string | null; vendor?: string | null; url?: string | null; aliases?: string[]; status?: SystemStatus;
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** Fired when the session has expired; the app returns to the login page. */
export const UNAUTHORIZED_EVENT = 'mv:unauthorized';

export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  // The one place the current environment joins a request: every tenant data route, none of the tenant-level ones
  const env = takesEnv(path) ? getEnvId() : null;
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: { ...(body === undefined ? {} : { 'content-type': 'application/json' }), ...(env ? { 'X-MV-Env': env } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/api/auth/login')) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError(res.status, (data && data.error) || res.statusText || `HTTP ${res.status}`);
  }
  return data as T;
}

const t = (tenantId: string) => `/api/tenants/${encodeURIComponent(tenantId)}`;

export const Api = {
  me: () => api<Me>('GET', '/api/auth/me'),
  login: (email: string, password: string) => api<Me>('POST', '/api/auth/login', { email, password }),
  logout: () => api<void>('POST', '/api/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api<void>('POST', '/api/auth/password', { currentPassword, newPassword }),

  search: (tenantId: string, params: URLSearchParams) => api<SearchResult>('GET', `${t(tenantId)}/search?${params}`),
  overview: (tenantId: string, params: URLSearchParams) => api<Overview>('GET', `${t(tenantId)}/overview?${params}`),
  values: (tenantId: string, params: URLSearchParams) => api<{ values: FieldValue[]; vaultErrors: VaultError[] }>('GET', `${t(tenantId)}/values?${params}`),
  history: (tenantId: string, params: URLSearchParams) => api<History>('GET', `${t(tenantId)}/history?${params}`),
  card: (tenantId: string, params: URLSearchParams) => api<Card>('GET', `${t(tenantId)}/card?${params}`),
  /** The runs of an overview's records (ref=) or of a search with its trail (q, and, andValue as for the overview), with from, to and max. */
  runs: (tenantId: string, params: URLSearchParams) => api<Runs>('GET', `${t(tenantId)}/runs?${params}`),
  /** The runs of a search, without an overview. */
  runsOf: (tenantId: string, q: string, params = new URLSearchParams()) =>
    api<Runs>('GET', `${t(tenantId)}/runs?${new URLSearchParams([...params, ['q', q]])}`),
  /** The tenant now: from=, to= as for a search (the last day by default); tz= for the hours of the day in the flows' rhythms. */
  activity: (tenantId: string, params = new URLSearchParams()) => api<Activity>('GET', `${t(tenantId)}/activity?${params}`),
  /** The integrations between systems: from=, to= (the last 7 days by default). */
  integrations: (tenantId: string, params = new URLSearchParams()) => api<Integrations>('GET', `${t(tenantId)}/integrations?${params}`),
  /** One integration (a process): from=, to= (the last 24 hours by default). */
  integration: (tenantId: string, process: string, params = new URLSearchParams()) =>
    api<IntegrationDetail>('GET', `${t(tenantId)}/integrations/${encodeURIComponent(process)}?${params}`),
  /** One system (a code or an alias): from=, to= (the last 24 hours by default). */
  systemOverview: (tenantId: string, code: string, params = new URLSearchParams()) =>
    api<SystemOverview>('GET', `${t(tenantId)}/systems/${encodeURIComponent(code)}/overview?${params}`),
  /** What a search is, fast: q, and, andValue, from, to as for the overview. */
  preview: (tenantId: string, params: URLSearchParams) => api<Preview>('GET', `${t(tenantId)}/preview?${params}`),
  /** Values by prefix while typing; from= limits the time range (the last week by default). */
  suggest: (tenantId: string, prefix: string, from?: string) =>
    api<Suggest>('GET', `${t(tenantId)}/suggest?${new URLSearchParams({ prefix, ...(from ? { from } : {}) })}`),
  records: (tenantId: string, params: URLSearchParams) => api<Records>('GET', `${t(tenantId)}/records?${params}`),
  gaps: (tenantId: string, params: URLSearchParams) => api<{ gaps: Gap[]; vaultErrors: VaultError[] }>('GET', `${t(tenantId)}/gaps?${params}`),
  virtualModels: (tenantId: string) => api<VirtualModel[]>('GET', `${t(tenantId)}/virtual-models`),
  saveVirtualModel: (tenantId: string, m: { id?: string; name: string; fields: VirtualModel['fields']; fromSuggestion?: string }) =>
    api<VirtualModel>('POST', `${t(tenantId)}/virtual-models`, m),
  modelSuggestions: (tenantId: string) => api<ModelSuggestions>('GET', `${t(tenantId)}/virtual-models/suggestions`),
  dismissSuggestion: (tenantId: string, id: string) => api<{ ok: true }>('POST', `${t(tenantId)}/virtual-models/suggestions/${encodeURIComponent(id)}/dismiss`, {}),
  undismissSuggestion: (tenantId: string, id: string) => api<{ ok: true }>('DELETE', `${t(tenantId)}/virtual-models/suggestions/${encodeURIComponent(id)}/dismiss`),
  entities: (tenantId: string, params: URLSearchParams) => api<EntityLayer>('GET', `${t(tenantId)}/entities?${params}`),
  lens: (tenantId: string, id: string, params: URLSearchParams) => api<Lens>('GET', `${t(tenantId)}/virtual-models/${id}/lens?${params}`),
  modelVersions: (tenantId: string, id: string) => api<ModelVersions>('GET', `${t(tenantId)}/virtual-models/${encodeURIComponent(id)}/versions`),
  publishModel: (tenantId: string, id: string, comment: string) =>
    api<{ id: string; version: number }>('POST', `${t(tenantId)}/virtual-models/${encodeURIComponent(id)}/publish`, { comment }),
  previewDeployment: (tenantId: string, environmentId: string, items: DeployItem[]) =>
    api<DeployPreview>('POST', `${t(tenantId)}/deployments/preview`, { environmentId, items }),
  deploy: (tenantId: string, environmentId: string, items: DeployItem[], confirm?: boolean) =>
    api<DeployResult>('POST', `${t(tenantId)}/deployments`, { environmentId, items, ...(confirm ? { confirm: true } : {}) }),
  /** All the tenant's own data models with versions and deployments, as an export document. */
  exportModels: (tenantId: string) => api<unknown>('GET', `${t(tenantId)}/virtual-models/export`),
  importModels: (tenantId: string, document: unknown, deploy: boolean, confirm: boolean) =>
    api<ModelsImportReport>('POST', `${t(tenantId)}/virtual-models/import`, { document, deploy, confirm }),
  /** The rebuild job of every online Vault of the environment (start / cancel / status). */
  rebuildStatus: (tenantId: string) => api<{ vaults: RebuildVault[]; offline: string[] }>('GET', `${t(tenantId)}/data-model/rebuild`),
  rebuildAct: (tenantId: string, action: 'start' | 'cancel', cls?: string) =>
    api<{ action: string; vaults: RebuildVault[]; offline: string[] }>('POST', `${t(tenantId)}/data-model/rebuild`, { action, ...(cls ? { class: cls } : {}) }),
  /** The distinct values of a field of a data model, from the newest documents that have it. */
  fieldValues: (tenantId: string, cls: string, path: string) =>
    api<{ class: string; path: string; limit: number; values: { value: string; count: number; first: number; last: number }[]; vaultErrors: VaultError[] }>(
      'GET', `${t(tenantId)}/data-model/values?${new URLSearchParams({ class: cls, path })}`),
  /** Forgets fields of a data model in every online Vault of the environment (the data stays searchable). */
  forgetFields: (tenantId: string, cls: string, paths: string[]) =>
    api<{ class: string; vaults: { vaultId: string; vaultName: string; removed: number; error?: string }[]; offline: string[] }>('POST', `${t(tenantId)}/data-model/forget`, { class: cls, paths }),
  deleteVirtualModel: (tenantId: string, id: string) => api<void>('DELETE', `${t(tenantId)}/virtual-models/${id}`),
  related: (tenantId: string, params: URLSearchParams) => api<Related>('GET', `${t(tenantId)}/related?${params}`),
  execution: (tenantId: string, executionId: string) =>
    api<Execution>('GET', `${t(tenantId)}/executions/${encodeURIComponent(executionId)}`),
  message: (tenantId: string, vaultId: string, uuid: string, day?: string) =>
    api<Message>('GET', `${t(tenantId)}/vaults/${vaultId}/messages/${encodeURIComponent(uuid)}${day ? `?day=${day}` : ''}`),
  payloadUrl: (tenantId: string, vaultId: string, uuid: string, day?: string, download = false) =>
    `${t(tenantId)}/vaults/${vaultId}/messages/${encodeURIComponent(uuid)}/payload?${new URLSearchParams({
      ...(day ? { day } : {}), ...(download ? { download: '1' } : {}), ...(getEnvId() ? { env: getEnvId()! } : {}),
    })}`,

  /** The vaults of the current environment, or of all of them (`all`, owners). */
  vaults: (tenantId: string, scope?: 'all') => api<VaultInfo[]>('GET', `${t(tenantId)}/vaults${scope ? `?env=${scope}` : ''}`),
  moveVault: (tenantId: string, vaultId: string, environmentId: string) =>
    api<VaultInfo>('PATCH', `${t(tenantId)}/vaults/${vaultId}`, { environmentId }),
  vault: (tenantId: string, vaultId: string) => api<VaultDetailData>('GET', `${t(tenantId)}/vaults/${vaultId}`),
  updateVault: (tenantId: string, vaultId: string, change: { name?: string | null; description?: string | null }) =>
    api<VaultInfo>('PATCH', `${t(tenantId)}/vaults/${vaultId}`, change),
  vaultNotes: (tenantId: string, vaultId: string) => api<VaultNote[]>('GET', `${t(tenantId)}/vaults/${vaultId}/notes`),
  addVaultNote: (tenantId: string, vaultId: string, text: string) => api<VaultNote>('POST', `${t(tenantId)}/vaults/${vaultId}/notes`, { text }),
  deleteVaultNote: (tenantId: string, vaultId: string, noteId: number) => api<void>('DELETE', `${t(tenantId)}/vaults/${vaultId}/notes/${noteId}`),
  vaultConfig: (tenantId: string, vaultId: string) => api<VaultConfigData>('GET', `${t(tenantId)}/vaults/${vaultId}/config`),
  /** Merges: a value sets an override, null removes it. Only the keys that changed. */
  saveVaultConfigData: (tenantId: string, vaultId: string, overrides: Record<string, SettingValue | null>) =>
    api<{ version: number; pushed: boolean }>('PUT', `${t(tenantId)}/vaults/${vaultId}/config`, { overrides }),
  restartVault: (tenantId: string, vaultId: string, reason?: string) =>
    api<{ id: string }>('POST', `${t(tenantId)}/vaults/${vaultId}/restart`, reason ? { reason } : {}),
  vaultLogs: (tenantId: string, vaultId: string, lines: number) =>
    api<{ lines: string[] }>('GET', `${t(tenantId)}/vaults/${vaultId}/logs?lines=${lines}`),
  vaultStatus: (tenantId: string, vaultId: string) => api<VaultStatus>('GET', `${t(tenantId)}/vaults/${vaultId}/status`),
  /** Asks the Vault to renew its ACME certificate now (202); 409 with the Vault's message when ACME is off, an order runs or the Vault is offline. */
  renewCertificate: (tenantId: string, vaultId: string) =>
    api<{ ok: true }>('POST', `${t(tenantId)}/vaults/${vaultId}/certificate/renew`, {}),
  revokeVault: (tenantId: string, vaultId: string) => api<void>('DELETE', `${t(tenantId)}/vaults/${vaultId}`),
  setVaultIngestUrl: (tenantId: string, vaultId: string, ingestUrl: string | null) =>
    api<VaultInfo>('PATCH', `${t(tenantId)}/vaults/${vaultId}`, { ingestUrl }),
  connectorKeys: (tenantId: string) => api<ConnectorKey[]>('GET', `${t(tenantId)}/connector-keys`),
  createConnectorKey: (tenantId: string, name: string, environmentId?: string, vaultIds?: string[] | null) =>
    api<ConnectorKey & { secret: string }>('POST', `${t(tenantId)}/connector-keys`, { name, ...(environmentId ? { environmentId } : {}), ...(vaultIds ? { vaultIds } : {}) }),
  updateConnectorKey: (tenantId: string, keyId: string, change: { name?: string; vaultIds?: string[] | null }) =>
    api<ConnectorKey>('PATCH', `${t(tenantId)}/connector-keys/${encodeURIComponent(keyId)}`, change),
  revokeConnectorKey: (tenantId: string, keyId: string) =>
    api<void>('DELETE', `${t(tenantId)}/connector-keys/${encodeURIComponent(keyId)}`),
  createInstallToken: (tenantId: string, ttlHours: number, environmentId?: string) =>
    api<{ token: string; expiresAt: number; environmentId: string }>('POST', `${t(tenantId)}/install-tokens`, { ttlHours, ...(environmentId ? { environmentId } : {}) }),

  environments: (tenantId: string) => api<Environment[]>('GET', `${t(tenantId)}/environments`),
  createEnvironment: (tenantId: string, e: { name: string; color?: string; protected?: boolean }) =>
    api<Environment>('POST', `${t(tenantId)}/environments`, e),
  updateEnvironment: (tenantId: string, envId: string, c: EnvironmentChange) =>
    api<Environment>('PATCH', `${t(tenantId)}/environments/${encodeURIComponent(envId)}`, c),
  deleteEnvironment: (tenantId: string, envId: string) =>
    api<{ deleted: string }>('DELETE', `${t(tenantId)}/environments/${encodeURIComponent(envId)}`),
  audit: (tenantId: string, limit = 50, before?: number) =>
    api<AuditEntry[]>('GET', `${t(tenantId)}/audit?${new URLSearchParams({ limit: String(limit), ...(before ? { before: String(before) } : {}) })}`),

  model: (tenantId: string) => api<ModelSnapshot>('GET', `${t(tenantId)}/model`),
  /** The data model catalog (docs/catalog.md); inEnvironment is about the current environment. */
  catalog: (tenantId: string) => api<CatalogData>('GET', `${t(tenantId)}/catalog`),
  updateCatalog: (tenantId: string, cls: string, patch: {
    displayName?: string; displayPlural?: string; description?: string | null; domain?: string | null; tags?: string[];
    steward?: string | null; sensitivity?: Sensitivity | null; status?: CatalogStatus; system?: string | null;
  }) => api<CatalogEntry>('PATCH', `${t(tenantId)}/catalog/${encodeURIComponent(cls)}`, patch),
  setCatalogStatus: (tenantId: string, classes: string[], status: CatalogStatus) =>
    api<{ status: CatalogStatus; changed: string[] }>('POST', `${t(tenantId)}/catalog/status`, { classes, status }),
  /** Copies the suggestions into the real fields where an admin has not set a value; the answer has the changed entries. */
  acceptSuggestions: (tenantId: string, classes: string[], what: import('./catalog.ts').SuggestionPart[] = ['domain', 'sensitivity', 'tags']) =>
    api<{ changed: CatalogEntry[] }>('POST', `${t(tenantId)}/catalog/accept`, { classes, what }),

  /** The system registry (docs/systems.md); detect=false: the registry alone, at once (no data models per system). */
  systems: (tenantId: string, detect = true) => api<SystemsData>('GET', `${t(tenantId)}/systems${detect ? '' : '?detect=0'}`),
  createSystem: (tenantId: string, s: SystemChange & { code: string; name: string }) => api<SystemInfo>('POST', `${t(tenantId)}/systems`, s),
  updateSystem: (tenantId: string, code: string, patch: SystemChange) => api<SystemInfo>('PATCH', `${t(tenantId)}/systems/${encodeURIComponent(code)}`, patch),
  setSystemStatus: (tenantId: string, codes: string[], status: SystemStatus) =>
    api<{ status: SystemStatus; changed: string[] }>('POST', `${t(tenantId)}/systems/status`, { codes, status }),
  /** Uploads a logo (PNG, JPEG or WebP, at most 200 KB): the body is the file itself. */
  putSystemLogo: async (tenantId: string, code: string, file: Blob): Promise<SystemInfo> => {
    const res = await fetch(`${t(tenantId)}/systems/${encodeURIComponent(code)}/logo`, { method: 'PUT', credentials: 'same-origin', headers: { 'content-type': file.type }, body: file });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      if (res.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
      throw new ApiError(res.status, data?.error ?? `HTTP ${res.status}`);
    }
    return data as SystemInfo;
  },
  deleteSystemLogo: (tenantId: string, code: string) => api<SystemInfo>('DELETE', `${t(tenantId)}/systems/${encodeURIComponent(code)}/logo`),

  users: (tenantId: string) => api<User[]>('GET', `${t(tenantId)}/users`),
  createUser: (tenantId: string, u: { email: string; name: string; role: Role; password: string }) =>
    api<User>('POST', `${t(tenantId)}/users`, u),
  deleteUser: (tenantId: string, userId: string) => api<void>('DELETE', `${t(tenantId)}/users/${userId}`),

  tenants: () => api<Tenant[]>('GET', '/admin/tenants'),
  createTenant: (name: string) => api<Tenant>('POST', '/admin/tenants', { name }),
  admins: () => api<User[]>('GET', '/admin/users'),
  createAdmin: (u: { email: string; name: string; password: string }) => api<User>('POST', '/admin/users', u),
  deleteAdmin: (userId: string) => api<void>('DELETE', `/admin/users/${userId}`),
};
